// src/features/dzban/pages/DzbanHome.jsx
// DARK THEME VERSION - "The Asphalt Arena"
// Main voting page - Dzban Roku
// SEO OPTIMIZED - MAY 2026

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trophy, Flame, TrendingUp, MapPin, Car } from 'lucide-react';
import { useDzbanVoting } from '../hooks/useDzbanVoting';
import { VoteBattle } from '../components/VoteBattle';

export const DzbanHome = ({ showToast }) => {
  const navigate = useNavigate();
  const { randomSpots, loading, vote, votesCount, canVote, maxVotes, fetchRandomSpots } = useDzbanVoting();
  const [voting, setVoting] = useState(false);
  const [votedSpots, setVotedSpots] = useState([]);

  // Fetch random spots on mount
  useEffect(() => {
    fetchRandomSpots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVote = async (winnerId, loserId) => {
    setVoting(true);
    
    const success = await vote(winnerId, loserId);
    
    if (success) {
      showToast('✅ Głos zaliczony!', 'success');
      
      // Add voted spot (WINNER = the "dzban" who got the vote) to history
      const votedSpot = randomSpots.find(s => s.id === winnerId);
      if (votedSpot) {
        setVotedSpots(prev => {
          const updated = [...prev, {
            id: votedSpot.id,
            image: votedSpot.images?.[0] || votedSpot.imageUrl,
            plate: votedSpot.plate
          }];
          // Keep only last 20
          return updated.slice(-20);
        });
      }
    } else {
      showToast('❌ Błąd głosowania. Spróbuj ponownie.', 'error');
    }
    
    setVoting(false);
  };

  // Calculate progress percentage
  const progressPercent = (votesCount / maxVotes) * 100;

  return (
    <>
      <Helmet>
        {/* SEO OPTIMIZED - HIGH VOLUME KEYWORDS */}
        <title>Chamstwo Drogowe - Dzban Roku 2026 | Głosuj na Najgorsze Parkowanie Polski</title>
        <meta name="description" content={`Społeczne głosowanie na najgorsze chamstwo drogowe w Polsce! Mistrz parkowania, bloker, agresor - zagłosuj na dzban roku 2026. ${votesCount}/${maxVotes} głosów dzisiaj. Ranking TOP 5, miast i marek. Warszawa vs Kraków - które miasto ma gorszych kierowców?`} />
        <meta name="keywords" content="chamstwo drogowe, dzban roku 2026, mistrz parkowania, złe parkowanie, najgorsi kierowcy polska, parkowanie ranking, głosowanie społeczne, ranking miast parkowanie, bmw parkowanie, audi parkowanie, warszawa parkowanie, aplikacja chamstwo drogowe" />
        
        {/* Open Graph - Social Media SEO */}
        <meta property="og:title" content="Chamstwo Drogowe - Dzban Roku 2026 | Który Gorszy? Ty Decydujesz!" />
        <meta property="og:description" content={`Zagłosuj na najgorsze chamstwo drogowe! Mistrz parkowania vs bloker vs agresor. ${votesCount}/${maxVotes} głosów dzisiaj. Dołącz do społeczności walczącej z chamskim parkowaniem!`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://motoradar24.pl/dzban-roku" />
        <meta property="og:image" content="https://motoradar24.pl/og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dzban Roku 2026 - Głosuj na Najgorsze Chamstwo Drogowe" />
        <meta name="twitter:description" content="Który gorszy? Zagłosuj na mistrza parkowania i dzbana roku!" />
        
        {/* Canonical + Robots */}
        <link rel="canonical" href="https://motoradar24.pl/dzban-roku" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
      </Helmet>

      {/* DARK BACKGROUND WITH SUBTLE TEXTURE */}
      <div className="min-h-screen pb-32 lg:pb-4 relative overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
        {/* Background texture overlay */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.02) 10px, rgba(255,255,255,.02) 20px)',
          }}
        />

        {/* HEADER - Dark with fire theme - COMPRESSED */}
        <div className="relative bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] text-white py-10 px-4 border-b border-gray-800">
          <div className="max-w-4xl mx-auto text-center relative z-10">
            {/* Fire emojis with glow */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <Flame 
                size={40} 
                className="text-orange-500 animate-pulse" 
                style={{ filter: 'drop-shadow(0 0 10px rgba(249, 115, 22, 0.5))' }}
                aria-hidden="true"
              />
              {/* H1 with SEO keywords */}
              <h1 
                className="text-5xl font-black uppercase tracking-tight"
                style={{ 
                  fontFamily: '"Bebas Neue", "Impact", sans-serif',
                  textShadow: '0 0 20px rgba(255, 255, 255, 0.2)',
                  letterSpacing: '3px'
                }}
              >
                DZBAN ROKU{' '}
                <span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">
                  2026
                </span>
              </h1>
              <Flame 
                size={40} 
                className="text-orange-500 animate-pulse" 
                style={{ filter: 'drop-shadow(0 0 10px rgba(249, 115, 22, 0.5))' }}
                aria-hidden="true"
              />
            </div>
            
            {/* Subtitle with keywords */}
            <p className="text-lg text-orange-200 mb-1 italic">
              Chamstwo drogowe - który gorszy? <span className="font-bold text-white">TY DECYDUJESZ!</span>
            </p>
            
            <p className="text-xs text-gray-400 max-w-2xl mx-auto mb-6">
              Głosuj na najgorsze parkowanie, mistrza parkowania, blokera i inne chamstwo drogowe. Społeczność wybiera najgorszych kierowców Polski!
            </p>

            {/* VOTE COUNTER - Glassmorphism card with progress bar - COMPRESSED */}
            <div className="inline-block">
              <div 
                className="bg-black/60 backdrop-blur-xl rounded-2xl border-2 border-orange-500/30 overflow-hidden"
                style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 20px rgba(249, 115, 22, 0.2)' }}
              >
                {/* Progress bar */}
                <div className="h-1.5 bg-gray-800 relative">
                  <div 
                    className="h-full bg-gradient-to-r from-red-600 to-orange-500 transition-all duration-500"
                    style={{ 
                      width: `${progressPercent}%`,
                      boxShadow: '0 0 10px rgba(249, 115, 22, 0.8)'
                    }}
                    role="progressbar"
                    aria-valuenow={votesCount}
                    aria-valuemin="0"
                    aria-valuemax={maxVotes}
                  />
                </div>

                {/* Counter content - COMPRESSED */}
                <div className="px-6 py-3">
                  <div className="text-[10px] text-orange-200 uppercase tracking-widest mb-1 font-semibold">
                    Twoje głosy dzisiaj
                  </div>
                  <div className="flex items-center gap-2 justify-center mb-3">
                    <div 
                      className="text-4xl font-black text-white"
                      style={{ 
                        fontFamily: '"Orbitron", monospace',
                        textShadow: '0 0 15px rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      {votesCount}
                    </div>
                    <div className="text-xl text-gray-400 font-bold">/ {maxVotes}</div>
                    <Flame size={22} className="text-orange-500" aria-hidden="true" />
                  </div>
                  
                  {/* Voted spots thumbnails - 20 max */}
                  {votedSpots.length > 0 && (
                    <div className="grid grid-cols-10 gap-1 mb-3">
                      {votedSpots.map((spot, idx) => (
                        <button
                          key={`${spot.id}-${idx}`}
                          onClick={() => navigate(`/pojazd/${spot.id}`)}
                          className="w-full aspect-square rounded overflow-hidden opacity-60 hover:opacity-100 hover:ring-2 hover:ring-orange-500 transition-all"
                          title={`Zobacz spot ${spot.plate}`}
                          aria-label={`Zobacz spot ${spot.plate}`}
                        >
                          <img 
                            src={spot.image || 'https://via.placeholder.com/40'} 
                            alt={`Chamstwo drogowe ${spot.plate}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                      {/* Fill remaining slots with placeholders */}
                      {Array(Math.max(0, 20 - votedSpots.length)).fill(null).map((_, idx) => (
                        <div 
                          key={`empty-${idx}`}
                          className="w-full aspect-square rounded bg-gray-800/30 border border-gray-700/30"
                          aria-hidden="true"
                        />
                      ))}
                    </div>
                  )}
                  
                  {!canVote && (
                    <div className="px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-lg text-[10px] text-red-300 font-semibold flex items-center justify-center gap-1">
                      ⚠️ Limit osiągnięty! Wróć jutro.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VOTING BATTLE SECTION - COMPRESSED */}
        <div className="max-w-6xl mx-auto p-4 py-6 relative z-10">
          {loading ? (
            <div className="text-center py-20">
              <div 
                className="animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent mx-auto mb-4"
                style={{ boxShadow: '0 0 20px rgba(249, 115, 22, 0.4)' }}
                role="status"
                aria-label="Ładowanie pojedynku"
              />
              <p className="text-gray-400 font-semibold">Ładowanie pojedynku...</p>
            </div>
          ) : randomSpots.length === 2 ? (
            <>
              <VoteBattle 
                spot1={randomSpots[0]} 
                spot2={randomSpots[1]} 
                onVote={handleVote}
                disabled={!canVote || voting}
              />

              {/* ACTION BUTTONS - Consistent dark theme - COMPRESSED */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
                {/* TOP 5 - Gold */}
                <button
                  onClick={() => navigate('/dzban-roku/ranking')}
                  className="group px-6 py-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-bold rounded-lg hover:from-amber-500 hover:to-yellow-500 transition-all flex items-center justify-center gap-3 relative overflow-hidden"
                  style={{ 
                    boxShadow: '0 4px 20px rgba(251, 191, 36, 0.3)',
                    height: '64px'
                  }}
                  aria-label="Zobacz TOP 5 najgorszych spotów"
                >
                  <Trophy size={24} className="group-hover:scale-110 transition-transform" aria-hidden="true" />
                  <span className="uppercase tracking-wide">TOP 5 Spotów</span>
                </button>
                
                {/* MIASTA - Blue */}
                <button
                  onClick={() => navigate('/dzban-roku/miasta')}
                  className="group px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-lg hover:from-blue-500 hover:to-indigo-500 transition-all flex items-center justify-center gap-3"
                  style={{ 
                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
                    height: '64px'
                  }}
                  aria-label="Zobacz ranking miast - chamstwo drogowe"
                >
                  <MapPin size={24} className="group-hover:scale-110 transition-transform" aria-hidden="true" />
                  <span className="uppercase tracking-wide">Ranking Miast</span>
                </button>

                {/* MARKI - Slate */}
                <button
                  onClick={() => navigate('/dzban-roku/marki')}
                  className="group px-6 py-4 bg-gradient-to-r from-slate-600 to-slate-700 text-white font-bold rounded-lg hover:from-slate-500 hover:to-slate-600 transition-all flex items-center justify-center gap-3"
                  style={{ 
                    boxShadow: '0 4px 20px rgba(100, 116, 139, 0.3)',
                    height: '64px'
                  }}
                  aria-label="Zobacz ranking marek aut - najgorsze parkowanie"
                >
                  <Car size={24} className="group-hover:scale-110 transition-transform" aria-hidden="true" />
                  <span className="uppercase tracking-wide">Ranking Marek</span>
                </button>
                
                {/* POWRÓT - Ghost */}
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-4 bg-transparent text-gray-400 font-bold rounded-lg hover:bg-white/5 hover:text-white transition-all border-2 border-gray-700 hover:border-gray-600"
                  style={{ height: '64px' }}
                  aria-label="Powrót do strony głównej"
                >
                  <span className="uppercase tracking-wide">Powrót</span>
                </button>
              </div>

              {/* INFO BOX - Dark glassmorphism - COMPRESSED */}
              <div 
                className="mt-6 bg-white/5 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/10"
                style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)' }}
              >
                <h2 className="font-bold text-white mb-2 flex items-center justify-center gap-2 text-sm">
                  <TrendingUp size={18} className="text-orange-500" aria-hidden="true" />
                  Jak działa głosowanie na Dzbana Roku?
                </h2>
                <p className="text-xs text-gray-300 max-w-2xl mx-auto leading-relaxed">
                  Głosuj na <span className="font-bold text-orange-400">gorsze chamstwo drogowe</span>! 
                  Każdy użytkownik może oddać <span className="font-bold text-white">20 głosów dziennie</span>. 
                  Spoty z największą liczbą głosów trafiają do rankingu <strong>Dzban Roku 2026</strong> - 
                  ranking miast (Warszawa vs Kraków), ranking marek (BMW vs Audi vs Mercedes) i TOP 5 najgorszych kierowców Polski.
                  <span className="block mt-2 text-orange-300 font-semibold">Najgorszy wygrywa! 🏆</span>
                </p>
              </div>
            </>
          ) : (
            <div 
              className="text-center py-20 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10"
              style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)' }}
            >
              <p className="text-gray-400 font-semibold mb-4 text-xl">
                Za mało spotów do głosowania 😢
              </p>
              <p className="text-sm text-gray-500">
                Dodaj więcej zgłoszeń chamstwa drogowego żeby uruchomić głosowanie na Dzbana Roku!
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};